import { useState } from "react";
import { useNavigate } from "react-router";
import { Trans, useLingui } from "@lingui/macro";
import { ChevronRight, RefreshCw } from "lucide-react";

import { Button } from "@app/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@app/components/ui/dialog";
import { WithheldCount } from "@app/components/WithheldCount";
import { cn } from "@app/lib/utils";
import type { PathwayPatientsResponse } from "@app/types/dashboard-pathway";
import { usePathwayPatients, type PathwayQuery } from "./usePathway";

/**
 * The dossiers behind the current selection — "show me those twenty".
 *
 * A click on a card filters the page, deliberately and always; this is the separate gesture for
 * seeing who is behind the bar. Rows carry the pathway's own summary rather than a name, and that is
 * not a privacy hedge: **indication → décision → geste → issue** is the same sentence the dashboard
 * just drew, so landing on the patient page continues a reading instead of starting a new one. Two
 * vocabularies for the same thing is what makes a product incomprehensible.
 *
 * The TRIGGER is the count itself, not a button beside it. "62 patients" is already written in the
 * counter and is exactly the moment a reader wants to see the sixty-two — the same idiom the
 * "sur 240 — tout afficher" two words to the right already uses.
 *
 * Where the way in is closed — the server names nobody there (`names_patients`), or the selection
 * matched nobody — the figure is still printed. It belongs to the counter rather than to this
 * dialog, and a count that disappeared with its own link would leave the sentence starting on
 * "· 87 épisodes".
 */
export default function PathwayPatientsModal({
  query,
  disabled,
  count,
  withheld,
  labelOf,
  actKey,
}: {
  query: PathwayQuery;
  /** True where the server would not name the patients (`names_patients`, PT-148). */
  disabled?: boolean;
  /** `null` while the counter is being read: it says nothing rather than « 0 ». */
  count: number | null;
  /** The count is under the anonymity threshold, read in aggregate (PT-144): said, not printed. */
  withheld?: boolean;
  /** Draws a term — the indication, the act — in the reader's language, as the cards do. */
  labelOf: (dimension: string, value: string) => string;
  /** The dimension the act is read on, which is the one the server names the act from. */
  actKey: string | null;
}) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data, loading, error, reload } = usePathwayPatients(query, !open || disabled);

  /* The counter's own words. Rendered identically whether or not they can be clicked, so the
     sentence a reader parses does not change shape with their perimeter. */
  const figure = (
    <>
      <span className="font-semibold text-slate-900 group-hover:text-primary">
        {withheld ? <WithheldCount /> : (count ?? "…")}
      </span>{" "}
      <Trans>patients</Trans>
    </>
  );

  if (disabled || !count) return figure;

  const payload = data as PathwayPatientsResponse | null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={t`Les dossiers de cette sélection`}
          className={cn(
            "group rounded-sm underline decoration-dotted decoration-slate-300 underline-offset-4",
            "transition-colors hover:text-primary hover:decoration-primary/50",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
          )}
        >
          {figure}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base">
            <Trans>Les dossiers de cette sélection</Trans>
          </DialogTitle>
        </DialogHeader>

        {loading && !payload && (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-slate-400">
            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            <Trans>Chargement...</Trans>
          </div>
        )}

        {/* The link only opens where the server names the patients, so an error here is a failure.
            Said, rather than an empty dialog that reads as a selection holding nobody. */}
        {error && !payload && !loading && (
          <div className="flex flex-col items-center gap-3 py-12 text-sm text-slate-500">
            <p>
              <Trans>La liste n'a pas pu être chargée.</Trans>
            </p>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={reload}>
              <RefreshCw className="h-3.5 w-3.5" />
              <Trans>Réessayer</Trans>
            </Button>
          </div>
        )}

        {payload && (
          <div className="max-h-[60vh] overflow-y-auto">
            {payload.patients.map((patient) => (
              <button
                key={`${patient.tenant_id}-${patient.id_patient}`}
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate(`/patient/${patient.tenant_id}/${patient.id_patient}`);
                }}
                className="flex w-full items-center gap-3 border-b border-slate-100 px-1 py-2 text-left hover:bg-primary/5"
              >
                {/* The NAME. A dossier number is a database key — no clinician recognises one, and a
                    list of them is a list nobody can act on. The number stays as a secondary line
                    because it is what the patient page URL is keyed on. */}
                <span className="w-44 shrink-0">
                  <span className="block truncate text-xs font-medium text-slate-900">
                    {patient.name ?? <Trans>Nom non renseigné</Trans>}
                  </span>
                  <span className="block text-3xs tabular-nums text-slate-400">
                    #{patient.id_patient}
                  </span>
                </span>
                <span className="min-w-0 flex-1">
                  {/* The same sentence the card drew, in the same order. */}
                  <span className="block truncate text-xs text-slate-700">
                    {patient.indication != null
                      ? labelOf("indication", patient.indication)
                      : t`indication non renseignée`}
                    {patient.act && (
                      <span className="text-slate-400">
                        {" "}
                        → {actKey ? labelOf(actKey, patient.act) : patient.act}
                      </span>
                    )}
                  </span>
                  <span className="block text-2xs text-slate-400">
                    {patient.decision}
                    {patient.outcome && ` · ${patient.outcome}`}
                    {patient.episodes > 1 && (
                      <>
                        {" · "}
                        <Trans>{patient.episodes} épisodes</Trans>
                      </>
                    )}
                  </span>
                </span>
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />
              </button>
            ))}
            {payload.truncated > 0 && (
              <p className="py-3 text-center text-2xs text-slate-400">
                <Trans>+ {payload.truncated} dossiers non listés — affinez la sélection</Trans>
              </p>
            )}
            {payload.patients.length === 0 && (
              <p className="py-12 text-center text-sm text-slate-400">
                <Trans>Aucun dossier dans cette sélection.</Trans>
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
